import { FileText, X } from "lucide-react";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import type { ProjectBrief } from "@/lib/api";

// Bookkeeping fields, not part of what the brief says.
const HIDDEN_BRIEF_FIELDS = [
	"id",
	"userId",
	"teamId",
	"conversationId",
	"briefType",
	"createdAt",
	"deleted",
	"status",
];

interface WorkspaceBriefModalProps {
	open: boolean;
	brief: ProjectBrief | undefined;
	onClose: () => void;
}

/** Read-only view of every field of the project brief. */
export function WorkspaceBriefModal({
	open,
	brief,
	onClose,
}: WorkspaceBriefModalProps) {
	if (!open || !brief) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/30 backdrop-blur-sm"
			onClick={onClose}
		>
			<motion.div
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.2 }}
				onClick={(e) => e.stopPropagation()}
				className="relative w-full max-w-3xl max-h-[85vh] mx-4 rounded-2xl bg-bg-surface border border-border-subtle shadow-popover flex flex-col"
			>
				{/* Modal Header */}
				<div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
					<div className="flex items-center gap-3">
						<FileText size={22} className="text-text-primary" />
						<h2 className="text-lg font-semibold text-text-primary">Brief</h2>
					</div>
					<Button variant="ghost" size="icon" onClick={onClose}>
						<X size={20} />
					</Button>
				</div>

				{/* Modal Body */}
				<div className="flex-1 overflow-y-auto px-6 py-4 scrollbar-thin">
					<div className="space-y-3">
						{Object.entries(brief)
							.filter(([key]) => !HIDDEN_BRIEF_FIELDS.includes(key))
							.map(([key, value]) => {
								const label = key
									.replace(/([A-Z])/g, " $1")
									.replace(/^./, (s) => s.toUpperCase())
									.trim();

								return (
									<div
										key={key}
										className="rounded-xl bg-bg-canvas border border-border-subtle px-4 py-3"
									>
										<p className="text-xs font-medium text-text-secondary mb-1">
											{label}
										</p>
										{Array.isArray(value) ? (
											<ul className="list-disc list-inside space-y-1">
												{value.map((item, i) => (
													<li
														key={i}
														className="text-sm text-text-secondary leading-relaxed"
													>
														{typeof item === "object"
															? JSON.stringify(item)
															: String(item)}
													</li>
												))}
											</ul>
										) : typeof value === "object" && value !== null ? (
											<ul className="list-disc list-inside space-y-1">
												{Object.entries(value as Record<string, unknown>).map(
													([k, v]) => (
														<li
															key={k}
															className="text-sm text-text-secondary leading-relaxed"
														>
															<span className="text-text-tertiary">{k}:</span>{" "}
															{String(v)}
														</li>
													),
												)}
											</ul>
										) : (
											<p className="text-sm text-text-secondary leading-relaxed">
												{String(value)}
											</p>
										)}
									</div>
								);
							})}
					</div>
				</div>
			</motion.div>
		</div>
	);
}
